import React, { useEffect, useRef, useState } from "react";
import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Collapse,
  Divider,
  Flex,
  Group,
  Modal,
  Paper,
  Select,
  Stack,
  Text,
  TextInput,
  ThemeIcon,
  Title,
  Tooltip,
} from "@mantine/core";
import {
  IconAlertCircle,
  IconDeviceLaptop,
  IconMinus,
  IconPlus,
  IconShieldCheck,
  IconX,
} from "@tabler/icons-react";
import {
  BhmrExternalDevice,
  dataOrderDetailTable,
} from "../../../../model/SaleOrderModel";
import { MessageResponse } from "../../../../model/MessageResponse";
import { repositoryPos } from "../../../../_base/_const/_constVar";
import { NotificationExtension } from "../../../../_base/extension/NotificationExtension";
import {
  countAssignedWarrantyForLaptop,
  getAssignedWarrantyCodes,
  getExternalSerialsForWarranty,
  getTotalAllocatedForWarranty,
  isLaptopItem,
  isWarrantyItem,
  setWarrantyAllocationForLaptop,
} from "../Helper/WarrantyHelper";

// Bỏ khoảng trắng hai đầu, cả ký tự trắng không hiển thị (máy quét / copy Excel hay chèn) mà trim() bỏ sót; viết hoa
const normalizeSerial = (value: string) =>
  value.replace(/^[\s​-‍⁠﻿]+|[\s​-‍⁠﻿]+$/g, "").toUpperCase();

// Kết quả GET /api/v1/TblItem/bhmr-device-by-serial
interface BhmrDeviceItem {
  itemId: number;
  itemCode: string;
  itemName: string;
}
interface BhmrDeviceBySerial {
  serial: string;
  isBhmrCard: boolean;
  items: BhmrDeviceItem[];
  activeLinks: { cardItemId: number; cardItemCode: string; cardSerial: string }[];
}

// Thông tin máy ngoài đơn trên các dòng gói cùng mã, theo serial viết hoa (dòng trước ưu tiên)
const getKnownExternalDevices = (
  list: dataOrderDetailTable[],
  wCodeUpper: string
): Map<string, BhmrExternalDevice> => {
  const known = new Map<string, BhmrExternalDevice>();
  for (const item of list) {
    if (!isWarrantyItem(item) || (item.itemCode || "").trim().toUpperCase() !== wCodeUpper) continue;
    for (const device of item.bhmrExternalDevices || []) {
      const key = (device?.deviceSerial || "").trim().toUpperCase();
      if (key && !known.has(key)) known.set(key, device);
    }
  }
  return known;
};

// Thông tin máy theo đúng thứ tự danh sách serial, bỏ serial chưa có thông tin
const pickExternalDevices = (
  known: Map<string, BhmrExternalDevice>,
  serials: string[]
): BhmrExternalDevice[] =>
  serials
    .map((s) => known.get(s.toUpperCase()))
    .filter((d): d is BhmrExternalDevice => !!d);

const getExternalDeviceLabel = (serial: string, device?: BhmrExternalDevice): string => {
  if (!device) return `SN: ${serial} – chưa có thông tin`;
  const name = (device.deviceItemName || "").trim();
  if (device.isSystemDevice) {
    return [`SN: ${serial}`, device.deviceItemCode, name].filter(Boolean).join(" – ");
  }
  return `SN: ${serial} – ${name} (ngoài hệ thống)`;
};

interface ModalAllocateWarrantyProps {
  opened: boolean;
  onClose: () => void;
  warrantyItem: dataOrderDetailTable | null;
  listAdd: dataOrderDetailTable[];
  setListAdd: React.Dispatch<React.SetStateAction<dataOrderDetailTable[]>>;
  /** Chỉ xem (đơn đã chốt / màn xem): không tăng giảm suất, không thêm / xoá serial máy cũ */
  readOnly?: boolean;
  /** false: không cho gán gói cho máy ngoài đơn (máy khách mua trước), dùng cho đơn đổi trả */
  allowExternalDevices?: boolean;
}

export const ModalAllocateWarranty: React.FC<ModalAllocateWarrantyProps> = ({
  opened,
  onClose,
  warrantyItem,
  listAdd,
  setListAdd,
  readOnly = false,
  allowExternalDevices = true,
}) => {
  const [showSerialInput, setShowSerialInput] = useState(false);
  const [serialNumber, setSerialNumber] = useState("");
  const [externalSerials, setExternalSerials] = useState<string[]>([]);
  // Kết quả tra serial máy cũ: null = chưa tra; items rỗng = ngoài hệ thống (nhập tên), nhiều mã = phải chọn mã
  const [lookup, setLookup] = useState<{ serial: string; items: BhmrDeviceItem[] } | null>(null);
  const [selectedDeviceItemId, setSelectedDeviceItemId] = useState<string | null>(null);
  const [deviceName, setDeviceName] = useState("");
  // Đang tra serial: bỏ qua xoá serial, khoá tăng giảm suất, vì lúc có kết quả sẽ ghi danh sách serial theo state lúc bấm Thêm
  const [isLookingUp, setIsLookingUp] = useState(false);
  // Tăng mỗi lần bỏ kết quả tra cứu, để bỏ qua phản hồi API về muộn
  const lookupSeqRef = useRef(0);

  // Luôn lấy bản ghi mới nhất của warrantyItem từ listAdd
  const currentWarranty =
    listAdd.find((it) => it.id === warrantyItem?.id) || warrantyItem;

  const warrantyCode = (currentWarranty?.itemCode || "").trim();
  const maxCapacity = currentWarranty?.quality || 1;

  // Khởi tạo serials máy cũ khi modal mở; kết quả tra serial bỏ cả khi đóng modal
  useEffect(() => {
    lookupSeqRef.current += 1;
    setLookup(null);
    setSelectedDeviceItemId(null);
    setDeviceName("");
    setIsLookingUp(false);
    if (opened && currentWarranty) {
      const serials = getExternalSerialsForWarranty(currentWarranty);
      setExternalSerials(serials);
      setShowSerialInput(false);
      setSerialNumber("");
    }
  }, [opened, currentWarranty?.id]);

  if (!currentWarranty) return null;

  const totalAllocated = getTotalAllocatedForWarranty(listAdd, warrantyCode);
  const remainingWarrantySlots = Math.max(0, maxCapacity - totalAllocated);

  const laptopItems = listAdd.filter((it) => isLaptopItem(it));
  const otherItems = listAdd.filter(
    (it) => !isLaptopItem(it) && !isWarrantyItem(it)
  );
  const knownDevices = getKnownExternalDevices(listAdd, warrantyCode.toUpperCase());

  const handleAdjustAllocation = (
    laptopItem: dataOrderDetailTable,
    delta: number
  ) => {
    const currentCount = countAssignedWarrantyForLaptop(
      laptopItem,
      warrantyCode
    );
    const assignedAll = getAssignedWarrantyCodes(laptopItem);
    const otherCodesCount = assignedAll.filter(
      (c) => c.toUpperCase() !== warrantyCode.toUpperCase()
    ).length;
    const maxCapacityLaptop = laptopItem.quality || 1;
    const remainingForLaptop = Math.max(0, maxCapacityLaptop - otherCodesCount);

    const newCount = currentCount + delta;

    if (newCount < 0) return;

    if (delta > 0) {
      if (currentCount >= remainingForLaptop) {
        NotificationExtension.Warn(
          `Không thể gán vượt quá số lượng máy (${maxCapacityLaptop} máy, đã gán ${otherCodesCount} gói khác)!`
        );
        return;
      }
      if (totalAllocated >= maxCapacity) {
        NotificationExtension.Warn(
          `Đã phân bổ hết ${maxCapacity}/${maxCapacity} suất của gói bảo hành này! Hãy tăng số lượng gói bảo hành ở cột 'Số lượng' trên bảng để có thêm suất.`
        );
        return;
      }
    }

    setListAdd((prevList) =>
      setWarrantyAllocationForLaptop(
        prevList,
        laptopItem.id,
        warrantyCode,
        newCount
      )
    );
  };

  const resetLookup = () => {
    lookupSeqRef.current += 1;
    setLookup(null);
    setSelectedDeviceItemId(null);
    setDeviceName("");
    setIsLookingUp(false);
  };

  const addExternalDevice = (device: BhmrExternalDevice) => {
    const cleanSerial = device.deviceSerial;
    const updatedSerials = [...externalSerials, cleanSerial];
    setExternalSerials(updatedSerials);
    setSerialNumber("");
    setShowSerialInput(false);
    resetLookup();

    // Đồng bộ serials vào attribute38 và thông tin máy vào bhmrExternalDevices của gói BH mà KHÔNG thay đổi quality
    setListAdd((prev) => {
      const wCodeUpper = warrantyCode.toUpperCase();
      const serialsStr = updatedSerials.join(", ");
      const known = getKnownExternalDevices(prev, wCodeUpper);
      known.set(cleanSerial.toUpperCase(), device);
      const devices = pickExternalDevices(known, updatedSerials);
      return prev.map((item) => {
        if (
          isWarrantyItem(item) &&
          item.itemCode?.trim().toUpperCase() === wCodeUpper
        ) {
          return {
            ...item,
            attribute38: serialsStr,
            bhmrExternalDevices: devices,
            note:
              updatedSerials.length > 0
                ? `Máy cũ: ${serialsStr}`
                : item.note || "",
          };
        }
        return item;
      });
    });

    NotificationExtension.Success(`Đã thêm máy cũ Serial: ${cleanSerial}`);
  };

  const handleAddSerial = async () => {
    if (isLookingUp) return;
    const cleanSerial = normalizeSerial(serialNumber);
    if (!cleanSerial) {
      NotificationExtension.Warn("Vui lòng nhập số serial của máy!");
      return;
    }
    if (externalSerials.some((s) => s.toUpperCase() === cleanSerial)) {
      NotificationExtension.Warn("Số serial này đã có trong danh sách!");
      return;
    }
    if (totalAllocated >= maxCapacity) {
      NotificationExtension.Warn(
        `Đã phân bổ hết ${maxCapacity}/${maxCapacity} suất của gói bảo hành này! Vui lòng tăng số lượng gói bảo hành ở cột 'Số lượng' nếu muốn gán thêm máy cũ.`
      );
      return;
    }
    // Lần bấm đầu với serial này: tra serial trong hệ thống
    if (!lookup || lookup.serial !== cleanSerial) {
      const seq = ++lookupSeqRef.current;
      setIsLookingUp(true);
      let res: MessageResponse<BhmrDeviceBySerial> | undefined;
      try {
        res = await repositoryPos.get<MessageResponse<BhmrDeviceBySerial>>(
          `/api/v1/TblItem/bhmr-device-by-serial?serial=${encodeURIComponent(cleanSerial)}`
        );
      } finally {
        if (seq === lookupSeqRef.current) setIsLookingUp(false);
      }
      // Đã sửa serial, bấm Hủy hoặc đóng modal trong lúc chờ
      if (seq !== lookupSeqRef.current) return;
      // Không tra được (API lỗi / chưa có): vẫn cho thêm như trước, chỉ cần nhập tên; khi lưu API tự xác định máy
      const data = res && res.success ? res.data : undefined;

      const items = data?.items || [];
      if (data?.isBhmrCard && items.length === 0) {
        NotificationExtension.Warn(`Serial ${cleanSerial} là thẻ BHMR, không phải máy!`);
        return;
      }
      const activeLink = (data?.activeLinks || []).find(
        (link) => Number(link.cardItemId) === Number(currentWarranty.itemId)
      );
      if (activeLink) {
        NotificationExtension.Warn(
          `Máy ${cleanSerial} đã có gói ${activeLink.cardItemCode} đang kích hoạt (thẻ ${activeLink.cardSerial})!`
        );
        return;
      }
      if (items.length === 1) {
        addExternalDevice({
          deviceSerial: cleanSerial,
          isSystemDevice: true,
          deviceItemId: items[0].itemId,
          deviceItemCode: items[0].itemCode,
          deviceItemName: items[0].itemName,
        });
        return;
      }
      setLookup({ serial: cleanSerial, items });
      setSelectedDeviceItemId(null);
      setDeviceName("");
      return;
    }

    // Lần bấm sau: thêm theo mã đã chọn (serial thuộc nhiều mã) hoặc tên đã nhập (ngoài hệ thống)
    if (lookup.items.length > 0) {
      const item = lookup.items.find((it) => String(it.itemId) === selectedDeviceItemId);
      if (!item) {
        NotificationExtension.Warn("Vui lòng chọn mã sản phẩm của máy!");
        return;
      }
      addExternalDevice({
        deviceSerial: cleanSerial,
        isSystemDevice: true,
        deviceItemId: item.itemId,
        deviceItemCode: item.itemCode,
        deviceItemName: item.itemName,
      });
      return;
    }
    const name = deviceName.trim();
    if (!name) {
      NotificationExtension.Warn("Vui lòng nhập tên sản phẩm của máy!");
      return;
    }
    addExternalDevice({
      deviceSerial: cleanSerial,
      isSystemDevice: false,
      deviceItemId: null,
      deviceItemCode: null,
      deviceItemName: name,
    });
  };

  const handleRemoveSerial = (idx: number) => {
    if (isLookingUp) return;
    const updatedSerials = externalSerials.filter((_, i) => i !== idx);
    setExternalSerials(updatedSerials);

    // Đồng bộ serials vào attribute38 và thông tin máy vào bhmrExternalDevices của gói BH mà KHÔNG thay đổi quality
    setListAdd((prev) => {
      const wCodeUpper = warrantyCode.toUpperCase();
      const serialsStr = updatedSerials.join(", ");
      const devices = pickExternalDevices(
        getKnownExternalDevices(prev, wCodeUpper),
        updatedSerials
      );
      return prev.map((item) => {
        if (
          isWarrantyItem(item) &&
          item.itemCode?.trim().toUpperCase() === wCodeUpper
        ) {
          return {
            ...item,
            attribute38: serialsStr,
            bhmrExternalDevices: devices,
            note:
              updatedSerials.length > 0
                ? `Máy cũ: ${serialsStr}`
                : "",
          };
        }
        return item;
      });
    });
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Group gap="xs">
          <ThemeIcon color="blue" variant="light" size="lg" radius="xl">
            <IconShieldCheck size={20} />
          </ThemeIcon>
          <div>
            <Title order={5}>
              Chia suất gói {currentWarranty.itemName}
              {readOnly ? " (chỉ xem)" : ""}
            </Title>
            <Text size="xs" c="dimmed">
              Mã gói: <Text span fw={600}>{warrantyCode}</Text> • Tổng số lượng mua:{" "}
              <Text span fw={700} c="blue.7">{maxCapacity}</Text> suất
            </Text>
          </div>
        </Group>
      }
      size="lg"
      radius="md"
      centered
      overlayProps={{
        backgroundOpacity: 0.55,
        blur: 3,
      }}
    >
      <Stack gap="sm" mt="xs">
        <Text size="xs" c="dimmed">
          Phân bổ {maxCapacity} suất của gói bảo hành vào các sản phẩm trong đơn
          hoặc máy cũ đã mua trước đó. Thao tác chia suất không làm thay đổi số
          lượng của gói trên đơn hàng.
        </Text>

        <Divider />

        {/* Danh sách Laptop và sản phẩm */}
        <Stack gap="xs" style={{ maxHeight: 380, overflowY: "auto" }}>
          {laptopItems.length === 0 && (
            <Paper p="sm" withBorder radius="sm" bg="yellow.0">
              <Group gap="xs">
                <IconAlertCircle size={18} color="#d97706" />
                <Text size="xs" c="yellow.9">
                  Đơn hàng hiện chưa có sản phẩm để gán gói. Bạn có thể phân bổ gói
                  này cho máy khách đã mua trước đó bằng cách nhập số Serial bên dưới.
                </Text>
              </Group>
            </Paper>
          )}

          {laptopItems.map((item, index) => {
            const currentAssigned = countAssignedWarrantyForLaptop(
              item,
              warrantyCode
            );
            const assignedAll = getAssignedWarrantyCodes(item);
            const otherAssignedCount = assignedAll.filter(
              (c) => c.toUpperCase() !== warrantyCode.toUpperCase()
            ).length;
            const maxQtyLaptop = item.quality || 1;
            const remainingForLaptop = Math.max(0, maxQtyLaptop - otherAssignedCount);

            const canAddMore =
              currentAssigned < remainingForLaptop && remainingWarrantySlots > 0;

            return (
              <Paper
                key={item.id || index}
                p="sm"
                withBorder
                radius="sm"
                style={{
                  backgroundColor:
                    currentAssigned > 0 ? "#f0fdf4" : "#ffffff",
                  borderColor:
                    currentAssigned > 0 ? "#86efac" : "#e5e7eb",
                }}
              >
                <Flex justify="space-between" align="center">
                  <Box style={{ flex: 1, paddingRight: 12 }}>
                    <Group gap="xs">
                      <IconDeviceLaptop size={18} color="#1d4ed8" />
                      <Text fw={600} size="sm" lineClamp={1}>
                        {item.itemName}
                      </Text>
                    </Group>
                    <Text size="xs" c="dimmed" mt={2}>
                      Mã: <Text span fw={500}>{item.itemCode}</Text> • SL máy: {maxQtyLaptop}
                      {otherAssignedCount > 0 && (
                        <Text span c="orange.7" fw={500}>
                          {" "}(đã gán {otherAssignedCount} gói khác)
                        </Text>
                      )}
                      {" • "}
                      <Text
                        span
                        fw={500}
                        c={currentAssigned > 0 ? "teal.7" : "gray"}
                      >
                        {currentAssigned > 0
                          ? `Đã gán ${currentAssigned} suất gói này`
                          : "Chưa gán gói này"}
                      </Text>
                    </Text>
                  </Box>

                  {/* Bộ tăng giảm suất chia; chỉ xem thì chỉ hiện số suất */}
                  <Group gap={6} align="center">
                    {!readOnly && (
                    <Tooltip label="Giảm 1 suất">
                      <ActionIcon
                        size="sm"
                        variant="default"
                        disabled={currentAssigned <= 0 || isLookingUp}
                        onClick={() => handleAdjustAllocation(item, -1)}
                      >
                        <IconMinus size={14} />
                      </ActionIcon>
                    </Tooltip>
                    )}
                    <Badge
                      size="lg"
                      variant="filled"
                      color={currentAssigned > 0 ? "blue" : "gray"}
                      style={{ minWidth: 32, textAlign: "center" }}
                    >
                      {currentAssigned}
                    </Badge>
                    {!readOnly && (
                    <Tooltip
                      label={
                        !canAddMore
                          ? currentAssigned >= remainingForLaptop
                            ? "Đã đạt tối đa số lượng máy"
                            : "Gói bảo hành đã hết suất trống (SL = " + maxCapacity + ")"
                          : "Tăng 1 suất"
                      }
                    >
                      <ActionIcon
                        size="sm"
                        variant="default"
                        disabled={!canAddMore || isLookingUp}
                        onClick={() => handleAdjustAllocation(item, 1)}
                      >
                        <IconPlus size={14} />
                      </ActionIcon>
                    </Tooltip>
                    )}
                  </Group>
                </Flex>
              </Paper>
            );
          })}

          {/* Các sản phẩm không áp dụng được (Màn hình, linh kiện...) */}
          {otherItems.map((item, index) => (
            <Paper
              key={item.id || index}
              p="xs"
              withBorder
              radius="sm"
              style={{
                backgroundColor: "#f9fafb",
                borderColor: "#f3f4f6",
                opacity: 0.65,
              }}
            >
              <Flex justify="space-between" align="center">
                <Box>
                  <Text size="sm" c="dimmed" lineClamp={1}>
                    {item.itemName}
                  </Text>
                  <Text size="xs" c="red.6" mt={2}>
                    Gói không áp dụng cho nhóm hàng này
                  </Text>
                </Box>
                <Text size="sm" c="dimmed" fw={600}>
                  —
                </Text>
              </Flex>
            </Paper>
          ))}

          {/* Máy khách đã mua trước đó (nhập serial) */}
          {externalSerials.length > 0 && (
            <Paper p="xs" withBorder radius="sm" bg="blue.0" mt="xs">
              <Text size="xs" fw={600} c="blue.8" mb={4}>
                Máy khách mua trước đó ({externalSerials.length} máy):
              </Text>
              <Group gap="xs" wrap="wrap">
                {externalSerials.map((serial, sIdx) => {
                  const label = getExternalDeviceLabel(
                    serial,
                    knownDevices.get(serial.toUpperCase())
                  );
                  return (
                    <Tooltip key={sIdx} label={label} multiline maw={400}>
                      <Badge
                        variant="filled"
                        color="blue"
                        tt="none"
                        maw="100%"
                        rightSection={
                          readOnly ? undefined : (
                          <ActionIcon
                            size="xs"
                            variant="transparent"
                            color="white"
                            onClick={() => handleRemoveSerial(sIdx)}
                            title="Xóa serial này"
                          >
                            <IconX size={10} color="#fff" />
                          </ActionIcon>
                          )
                        }
                      >
                        {label}
                      </Badge>
                    </Tooltip>
                  );
                })}
              </Group>
            </Paper>
          )}

          {/* Nút nhập serial máy cũ (ẩn khi chỉ xem hoặc đơn không áp dụng máy ngoài đơn) */}
          {!readOnly && allowExternalDevices && (
          <Box mt="xs">
            {!showSerialInput ? (
              <Button
                variant="subtle"
                size="xs"
                color="blue"
                disabled={remainingWarrantySlots <= 0}
                onClick={() => setShowSerialInput(true)}
              >
                + Máy khách đã mua trước đó — nhập serial
              </Button>
            ) : (
              <Collapse in={showSerialInput}>
                <Paper p="xs" withBorder radius="sm" bg="gray.0">
                  <Text size="xs" fw={500} mb={4}>
                    Nhập số Serial của máy đã mua trước đó:
                  </Text>
                  <Group gap="xs">
                    <TextInput
                      size="xs"
                      placeholder="VD: SN-12345678"
                      value={serialNumber}
                      onChange={(e) => {
                        setSerialNumber(e.target.value.toUpperCase());
                        resetLookup();
                      }}
                      readOnly={isLookingUp}
                      style={{ flex: 1 }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") handleAddSerial();
                      }}
                      autoFocus
                    />
                    <Button size="xs" onClick={handleAddSerial} loading={isLookingUp}>
                      Thêm
                    </Button>
                    <Button
                      size="xs"
                      variant="default"
                      onClick={() => {
                        setShowSerialInput(false);
                        resetLookup();
                      }}
                    >
                      Hủy
                    </Button>
                  </Group>
                  {lookup && lookup.items.length > 1 && (
                    <Select
                      mt="xs"
                      size="xs"
                      label="Mã sản phẩm"
                      description="Serial thuộc nhiều mã sản phẩm, chọn mã của máy rồi bấm Thêm"
                      placeholder="Chọn mã sản phẩm"
                      required
                      searchable
                      data={lookup.items.map((it) => ({
                        value: String(it.itemId),
                        label: `${it.itemCode} – ${it.itemName}`,
                      }))}
                      value={selectedDeviceItemId}
                      onChange={(value) => setSelectedDeviceItemId(value)}
                    />
                  )}
                  {lookup && lookup.items.length === 0 && (
                    <TextInput
                      mt="xs"
                      size="xs"
                      label="Tên sản phẩm"
                      description="Serial không có trong hệ thống, nhập tên sản phẩm của máy rồi bấm Thêm"
                      placeholder="VD: Laptop Dell Inspiron 15"
                      required
                      maxLength={1000}
                      value={deviceName}
                      onChange={(e) => setDeviceName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") handleAddSerial();
                      }}
                      autoFocus
                    />
                  )}
                </Paper>
              </Collapse>
            )}
          </Box>
          )}
        </Stack>

        <Divider my="xs" />

        {/* Footer */}
        <Flex justify="space-between" align="center">
          <Text size="sm" fw={600} c={totalAllocated === maxCapacity ? "green.8" : "blue.8"}>
            Đã phân bổ: {totalAllocated} / {maxCapacity} suất
            {remainingWarrantySlots > 0 && (
              <Text span fw={500} c="dimmed">
                {" "}(còn {remainingWarrantySlots} suất trống)
              </Text>
            )}
            {remainingWarrantySlots === 0 && (
              <Text span fw={500} c="teal.7">
                {" "}(đã đủ suất)
              </Text>
            )}
          </Text>

          <Button color="blue" onClick={onClose}>
            Xong
          </Button>
        </Flex>
      </Stack>
    </Modal>
  );
};

export default ModalAllocateWarranty;
